import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/lib/csvParser.js (auto-detect supports Xero, Sage, QuickBooks,
//     FreeAgent only -- no bank-specific parsers; strict header matching
//     so any other format falls through to the custom mapper)
//   - src/components/CsvColumnMapper.js (date format options: auto, DMY,
//     MDY, YMD; reverse-sign checkbox for credit-card-style statements;
//     header row picker for files with metadata rows above headers;
//     payee column also mappable, optional)
//   - src/pages/NewRequest.js (no automatic exclusion of negative or
//     positive amounts; user picks what to include)
// User confirmed:
//   - Step-by-step vendor menu paths were "general knowledge" and may be off
// Removed:
//   - All vendor-specific menu instructions (Xero/Sage/QB/FreeAgent). Replaced
//     with a pointer to each vendor's own help docs.
//   - "Receipt Chaser handles HSBC, Lloyds, NatWest, Barclays, Starling,
//     Monzo, Tide, Revolut" -- false. There are no bank-specific parsers.
//   - "Refunds excluded by default" -- false. Nothing is auto-excluded.
//   - "Date format selector available by default" -- only available in the
//     custom mapping flow, not the standard auto-detect path.
//   - "Receipt Chaser shows the first few rows so you can confirm column
//     mapping" -- only happens when auto-detect fails or the user picks
//     Custom mapping explicitly.
// Pass 2: re-read csvParser and CsvColumnMapper end to end. All mapper
// features (header row picker, field dropdowns, amount mode, date format,
// reverse-sign, live preview) already covered in the article. Nothing
// material missed.

export default {
  title:   'Importing transactions from your accounting software',
  summary: 'CSV exports from Xero, Sage, QuickBooks, FreeAgent and bank statements, and what to do if the import looks wrong.',
  body: (
    <>
      <P>
        Receipt Chaser doesn't connect directly to your accounting
        software. It works from CSV files you export yourself. This is
        deliberate: it keeps things simple, works for every platform
        without a separate integration, and means you stay in control of
        which transactions get sent to which client.
      </P>

      <P>
        A CSV needs three columns to work: the transaction{' '}
        <strong>date</strong>, a <strong>description</strong> the client
        will recognise, and the <strong>amount</strong>. Bank exports and
        accounting CSVs usually have these already.
      </P>

      <H>What's auto-detected, and what isn't</H>
      <P>
        Receipt Chaser recognises CSV exports from four accounting tools
        without any manual setup:
      </P>
      <UL>
        <Li><strong>Xero</strong></Li>
        <Li><strong>Sage</strong> (Business Cloud Accounting and Sage 50)</Li>
        <Li><strong>QuickBooks Online</strong></Li>
        <Li><strong>FreeAgent</strong></Li>
      </UL>
      <P>
        For any other format, including bank exports, credit card
        statements, and custom spreadsheets, Receipt Chaser falls back to
        a column-mapping screen where you tell it which column is which.
        This works for every UK bank you've come across as long as the
        CSV has the basic three columns.
      </P>

      <H>Exporting from your accounting software</H>
      <P>
        Menu paths change between versions, so rather than list steps
        that might be out of date, here are the vendor's own export
        guides:
      </P>
      <UL>
        <Li>
          <strong>Xero:</strong> search "export account transactions" in{' '}
          <a href="https://central.xero.com" target="_blank" rel="noopener noreferrer">Xero Central</a>
        </Li>
        <Li>
          <strong>Sage:</strong> the{' '}
          <a href="https://www.sage.com/en-gb/support/" target="_blank" rel="noopener noreferrer">Sage help centre</a>
          {' '}has different guides for Sage Business Cloud Accounting and
          Sage 50. Search for "export transactions to CSV".
        </Li>
        <Li>
          <strong>QuickBooks Online:</strong> the export option lives on
          the Reports page. See the{' '}
          <a href="https://quickbooks.intuit.com/learn-support/" target="_blank" rel="noopener noreferrer">QuickBooks support hub</a>
          {' '}for the current menu path.
        </Li>
        <Li>
          <strong>FreeAgent:</strong> the{' '}
          <a href="https://support.freeagent.com" target="_blank" rel="noopener noreferrer">FreeAgent knowledge base</a>
          {' '}walks through exporting bank transactions to CSV.
        </Li>
      </UL>

      <Callout variant="tip" title="Finding only unrecorded transactions">
        If you only want transactions that haven't been recorded against
        a receipt yet, every supported package has a way to narrow the
        export:
        <UL>
          <Li><strong>Xero:</strong> run the <em>Uncoded Statement Lines</em> report (under Accounting → Reports).</Li>
          <Li><strong>FreeAgent:</strong> run a bank reconciliation report (full report) and export only the unexplained lines.</Li>
          <Li><strong>Sage:</strong> from the banking view, export the unrecorded transactions only.</Li>
          <Li><strong>QuickBooks:</strong> from the banking view, export the transactions still awaiting review.</Li>
        </UL>
        These are exactly the lines a bookkeeper actually needs receipts
        for, which keeps the request short and avoids chasing for
        receipts your client has already supplied.
      </Callout>

      <H>Using a bank or credit card CSV</H>
      <P>
        Receipt Chaser doesn't have built-in parsers for specific banks.
        Instead, when you upload a non-accounting CSV, you'll see the
        column mapping screen. Tell it which columns hold the date,
        description, and amount, and the rest works the same as an
        accounting export.
      </P>
      <P>
        For credit card statements specifically, you'll usually want to
        tick <strong>Reverse sign of all amounts</strong> on the mapping
        screen. Credit card statements often show spending as positive
        numbers; ticking this flips them to negative so they read as
        expenditure rather than income.
      </P>

      <H>The column mapping screen</H>
      <P>
        When the mapper opens, you'll see:
      </P>
      <UL>
        <Li>
          <strong>A row picker for the headers.</strong> Some CSVs have
          metadata rows above the column headers (account name, date
          range, etc.). Click the row that contains your column names.
        </Li>
        <Li>
          <strong>Dropdowns for each required field.</strong> Pick which
          CSV column holds the date, description, and amount. Pick the
          payee column if you have one (optional).
        </Li>
        <Li>
          <strong>An amount mode toggle.</strong> If your CSV has a single
          signed amount column, leave it on "one signed column". If it has
          separate money-in and money-out columns, switch to the split
          mode.
        </Li>
        <Li>
          <strong>A date format selector.</strong> Auto-detect handles
          most cases, but you can force <em>DD/MM/YYYY</em>,{' '}
          <em>MM/DD/YYYY</em>, or <em>YYYY-MM-DD</em> if your file uses
          something unusual.
        </Li>
        <Li>
          <strong>A live preview.</strong> The first parsed transactions
          appear at the bottom of the screen and update as you change
          settings, so you can see immediately whether the mapping is
          correct.
        </Li>
      </UL>

      <Screenshot
        src="/support-screenshots/04 - custom-mapping.jpg"
        alt="The Map your CSV columns screen, showing the header-row picker at the top, the date / description / payee / amount dropdowns, a reverse-sign checkbox, a date format selector, and a live preview of the first parsed transactions at the bottom."
        caption="The column mapping screen with header row picker, field dropdowns, reverse-sign and date-format options, and a live preview that updates as you change settings."
      />

      <H>Choosing what to include</H>
      <P>
        After the file is read (whether auto-detected or mapped), you
        see the full transaction list with each row removable. Nothing
        is excluded automatically. Income and expenditure rows both
        appear, and a small badge indicates which is which. Remove any
        rows that shouldn't be in this request before continuing.
      </P>
      <P>
        Descriptions can't be edited at this stage. If a bank's wording
        is cryptic (something like <em>SQ *MERCHANT 1234</em>), you can
        clean it up later from the request detail page once the request
        has been created. See{' '}
        <Link to="/support/resending-or-editing-a-request">Resending or editing a request</Link>
        {' '}for the steps.
      </P>

      <H>If the import looks wrong</H>
      <P>
        Common issues and quick fixes:
      </P>
      <UL>
        <Li>
          <strong>Auto-detect picked the wrong format, or extracted no
          rows.</strong> Go back to step 1, change the software dropdown
          to <Code>🛠 Custom mapping</Code>, and re-upload. You'll get
          the mapping screen and can configure it manually.
        </Li>
        <Li>
          <strong>Dates look wrong (e.g. shifted by a month).</strong>{' '}
          Your CSV is probably in US format (MM/DD/YYYY). Use the custom
          mapping screen and set the date format dropdown to the right
          option.
        </Li>
        <Li>
          <strong>Credit card expenses showing as income (positive).</strong>{' '}
          Tick "Reverse sign of all amounts" on the mapping screen.
        </Li>
        <Li>
          <strong>Other problems.</strong> See{' '}
          <Link to="/support/csv-import-not-working">CSV import isn't working</Link>
          {' '}for a more detailed troubleshooting list.
        </Li>
      </UL>

      <H>Once your list looks right</H>
      <P>
        Continue with the rest of the request flow. The full guide to
        sending a request, including what your client sees and how
        reminders work, is in{' '}
        <Link to="/support/sending-your-first-request">Sending your first receipt request</Link>.
      </P>
    </>
  ),
}
